'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Two-signal "新版本可用" toast.
 *
 * Signal 1 — /api/version polling. Works in every browser (even without
 * a registered service worker). Polls every POLL_INTERVAL_MS + on focus.
 *
 * Signal 2 — service worker update. <ServiceWorkerRegister> dispatches
 * `agentx:sw-update-ready` when a new SW finishes installing while an
 * old controller is still active. We capture the registration so the
 * "更新" button can postMessage SKIP_WAITING (which activates the new
 * SW, clears the prior caches in its activate handler, and reloads via
 * the controllerchange listener in ServiceWorkerRegister).
 *
 * The toast shows when EITHER signal fires. Tapping "更新" picks the
 * right action: if a waiting SW exists, hand it the message; otherwise
 * just hard-reload (network-first SW will refetch the new HTML on next
 * request).
 *
 * Mounted from app/layout.tsx, so it runs on every page.
 *
 * V3.4 (2026-06-01) — two iteration-friendly fixes for the
 * "有新版本 keeps coming back" report:
 *   1. Dismissals persist in sessionStorage keyed by the exact
 *      serverBuildId. Once the user clicks 稍后 for build X, we don't
 *      re-toast for build X. A NEW build Y still toasts (as it should).
 *   2. Poll cadence relaxed to 5 minutes. The previous 60s pinged
 *      hard during active dev iterations and made the toast feel
 *      noisy. Focus + visibilitychange remain instantaneous so the
 *      user sees fresh state when they come back to the tab.
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const DISMISSED_KEY = 'agentx-update-dismissed-build';
// 2026-06-11 — once user dismisses, mute the toast for the rest of this
// tab session. Without this, every page navigation re-mounts the SW
// watcher and re-fires the agentx:sw-update-ready event for the same
// SW.waiting, popping the toast over and over for a single deploy.
const MUTED_KEY = 'agentx-update-muted-build';

type RecentCommit = { sha: string; subject: string; zh_summary?: string };

/**
 * V3.4 (2026-06-03) — translate conventional-commit subject prefix into a
 * Chinese category label so the changelog popup reads naturally.
 *
 *   "fix(voice): drop regenerate confirm"  →  "🔧 语音 · 修复"
 *   "feat(soul): redesign confidence"      →  "✨ SOUL · 新增"
 *
 * Anything that doesn't match falls through to the raw subject. Scope
 * map covers the modules we actually ship — add new ones as they appear.
 */
const TYPE_LABEL: Record<string, { emoji: string; zh: string }> = {
  feat: { emoji: '✨', zh: '新增' },
  fix: { emoji: '🔧', zh: '修复' },
  ui: { emoji: '🎨', zh: '界面' },
  ux: { emoji: '🎨', zh: '体验' },
  style: { emoji: '🎨', zh: '样式' },
  refactor: { emoji: '♻️', zh: '重构' },
  perf: { emoji: '⚡', zh: '性能' },
  docs: { emoji: '📝', zh: '文档' },
  test: { emoji: '🧪', zh: '测试' },
  chore: { emoji: '🔨', zh: '杂项' },
  ci: { emoji: '🚢', zh: '部署' },
  ops: { emoji: '🚢', zh: '运维' },
};
const SCOPE_LABEL: Record<string, string> = {
  voice: '语音',
  soul: 'SOUL 人格',
  vault: '知识库',
  kb: '知识库',
  training: '训练',
  train: '训练',
  studio: '撰写 Studio',
  printer: '撰写打印机模式',
  concept: '概念',
  persona: '人格设置',
  onboarding: '入驻',
  apply: '入驻申请',
  ux: '体验',
  ui: '界面',
  web: 'Web 前端',
  pwa: 'PWA',
  api: '接口',
  twin: '分身对话',
  landing: '官网首页',
  share: '分享设置',
};

function renderCommitHeading(subject: string): { label: string; rest: string } {
  const m = subject.match(/^(\w+)(?:\(([^)]+)\))?:\s*(.*)$/);
  if (!m) return { label: '', rest: subject };
  const type = (m[1] ?? '').toLowerCase();
  const scope = (m[2] ?? '').toLowerCase();
  const rest = m[3] ?? '';
  const t = TYPE_LABEL[type];
  const s = SCOPE_LABEL[scope] ?? scope;
  if (!t && !s) return { label: '', rest: subject };
  const parts: string[] = [];
  if (t) parts.push(`${t.emoji} ${s || type}`);
  else if (s) parts.push(s);
  if (t) parts.push(t.zh);
  return { label: parts.join(' · '), rest };
}

export function UpdateNotifier(): JSX.Element | null {
  const ownBuildId = process.env.NEXT_PUBLIC_BUILD_ID;
  const [serverBuildId, setServerBuildId] = useState<string | null>(null);
  const [recentCommits, setRecentCommits] = useState<RecentCommit[]>([]);
  const [swReg, setSwReg] = useState<ServiceWorkerRegistration | null>(null);
  const [dismissedBuild, setDismissedBuild] = useState<string | null>(null);
  // 2026-06-11 — once the user clicks 稍后, mute the toast until the next
  // genuine build flip. mutedBuild stores the build id at dismiss time;
  // a different serverBuildId later in the session unmutes naturally.
  const [mutedBuild, setMutedBuild] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  const check = useCallback(async () => {
    try {
      const res = await fetch('/api/version', { cache: 'no-store' });
      if (!res.ok) return;
      const body = (await res.json()) as {
        build_id?: string;
        recent_commits?: RecentCommit[];
      };
      if (body.build_id) setServerBuildId(body.build_id);
      if (Array.isArray(body.recent_commits)) setRecentCommits(body.recent_commits);
    } catch {
      // network blip — silent retry on next interval
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Hydrate the dismissal so navigating between pages keeps the
    // "稍后" choice intact for the same build_id.
    try {
      // 2026-06-11 — moved sessionStorage → localStorage so closing/reopening
      // the tab on the same build still respects the user's 稍后 choice.
      const v = window.localStorage.getItem(DISMISSED_KEY);
      if (v) setDismissedBuild(v);
      const m = window.localStorage.getItem(MUTED_KEY);
      if (m) setMutedBuild(m);
    } catch {
      /* private mode / quota */
    }

    // ── Signal 2: SW update ───────────────────────────────────────────
    function onSwReady(e: Event) {
      const detail = (e as CustomEvent<{ registration?: ServiceWorkerRegistration }>).detail;
      if (detail?.registration) setSwReg(detail.registration);
    }
    window.addEventListener('agentx:sw-update-ready', onSwReady);

    // ── Signal 1: /api/version polling ────────────────────────────────
    let cleanupPolling: (() => void) | null = null;
    if (ownBuildId && ownBuildId !== 'unknown') {
      const initial = setTimeout(check, 3000);
      const interval = setInterval(check, POLL_INTERVAL_MS);
      const onFocus = () => check();
      window.addEventListener('focus', onFocus);
      cleanupPolling = () => {
        clearTimeout(initial);
        clearInterval(interval);
        window.removeEventListener('focus', onFocus);
      };
    }

    return () => {
      window.removeEventListener('agentx:sw-update-ready', onSwReady);
      if (cleanupPolling) cleanupPolling();
    };
  }, [check, ownBuildId]);

  const buildMismatch =
    serverBuildId !== null && ownBuildId !== undefined && serverBuildId !== ownBuildId;
  // Suppress toast for the exact build the user already dismissed.
  // Falls back to false if no dismissal recorded yet.
  const dismissedThisBuild =
    serverBuildId !== null && dismissedBuild !== null && dismissedBuild === serverBuildId;
  // 2026-06-11 — muted overrides everything until a genuinely new build
  // shows up. SW-only signals share the mute (sentinel '__sw__'); once
  // serverBuildId resolves to something we haven't muted, the toast
  // unmutes naturally.
  const isMuted = mutedBuild !== null && (mutedBuild === '__sw__' || mutedBuild === serverBuildId);
  const hasUpdate = !isMuted && !dismissedThisBuild && (buildMismatch || !!swReg);
  if (!hasUpdate) return null;

  function persistMute(token: string) {
    setMutedBuild(token);
    try {
      window.localStorage.setItem(MUTED_KEY, token);
    } catch {
      /* private mode */
    }
  }

  function dismiss() {
    if (serverBuildId) {
      setDismissedBuild(serverBuildId);
      try {
        window.localStorage.setItem(DISMISSED_KEY, serverBuildId);
      } catch {
        /* private mode */
      }
      persistMute(serverBuildId);
    } else {
      // No serverBuildId yet (SW-only signal) — fall back to a sentinel
      // so the toast stays hidden until a real build flip arrives.
      setDismissedBuild('__sw__');
      persistMute('__sw__');
    }
    // Drop swReg locally so any in-flight SW signals in this render
    // don't re-show the toast.
    setSwReg(null);
  }

  function refresh() {
    setApplying(true);
    if (swReg) {
      // Tell the waiting SW to activate. Its activate handler clears all
      // prior caches; ServiceWorkerRegister's controllerchange listener
      // then reloads the page once the new SW takes over.
      const waiting = swReg.waiting ?? swReg.installing;
      if (waiting) {
        waiting.postMessage({ type: 'SKIP_WAITING' });
        // Belt-and-braces: if controllerchange doesn't fire within 2s
        // (some browsers swallow it during the very first claim), reload
        // ourselves so the user isn't stuck on the toast forever.
        setTimeout(() => window.location.reload(), 2000);
        return;
      }
    }
    // No waiting SW — version mismatch only. Hard reload picks up the
    // new bundle via the network-first fetch path.
    window.location.reload();
  }

  // V3.4 (2026-06-03) — slice recent_commits to everything AFTER the
  // server head down to (exclusive) the user's stale build_id. The
  // server list is newest-first; the entries we want are the prefix
  // until we hit the user's SHA. If we can't find the user's SHA
  // (their build is older than 30 commits, or 'unknown'), we fall back
  // to the top 8 — better to show too many than to surface nothing.
  //
  // 2026-07-12 — 在 prod（chiefeco.ai bare host）隐藏 changelog 列表；
  // staging (staging.chiefeco.ai) / local (localhost) 保持不变，方便迭代
  // 期看每次都更新了啥。
  const isProd = typeof window !== 'undefined' && window.location.hostname === 'chiefeco.ai';
  let changes: RecentCommit[] = [];
  if (!isProd && recentCommits.length > 0) {
    const cutIdx = ownBuildId ? recentCommits.findIndex((c) => c.sha === ownBuildId) : -1;
    changes = cutIdx >= 0 ? recentCommits.slice(0, cutIdx) : recentCommits.slice(0, 8);
    // De-noise: drop pure chore/style/ci commits — they don't change UX.
    changes = changes.filter((c) => !/^(chore|ci|style|docs|test)(\(|:)/i.test(c.subject));
    // Cap at 10 for popup height; the rest live in git log for the curious.
    changes = changes.slice(0, 10);
  }

  return (
    <output
      aria-live="polite"
      className="fixed z-50 page-in"
      style={{
        // Below the workspace header on mobile so it doesn't collide
        // with the hamburger row; bottom-right on desktop.
        right: 'max(env(safe-area-inset-right), 1rem)',
        bottom: 'calc(env(safe-area-inset-bottom) + 72px)',
        maxWidth: 'min(28rem, calc(100vw - 2rem))',
      }}
    >
      <div className="bg-white rounded-2xl border border-soft shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-full bg-warmth/15 text-warmth font-serif text-lg flex items-center justify-center flex-shrink-0"
            aria-hidden
          >
            ✨
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-base text-ink mb-1">有新版本</p>
            <p className="text-dusk text-xs leading-relaxed mb-2">
              点更新刷新页面并加载最新版本——会清掉旧缓存。
            </p>
            {changes.length > 0 && (
              <div className="mb-3 max-h-60 overflow-y-auto pr-1">
                <p className="text-dusk text-[11px] uppercase tracking-wider mb-1.5">本次更新</p>
                <ul className="space-y-2.5">
                  {changes.map((c) => {
                    const { label, rest } = renderCommitHeading(c.subject);
                    const detail = c.zh_summary?.trim() || rest;
                    return (
                      <li key={c.sha} className="text-ink text-xs leading-snug flex gap-1.5">
                        <span aria-hidden className="text-dusk shrink-0">
                          •
                        </span>
                        <span className="break-words">
                          {label && (
                            <span className="font-medium text-ink">
                              {label}
                              {detail ? ' — ' : ''}
                            </span>
                          )}
                          {detail && <span className="text-ink-mute">{detail}</span>}
                          {!label && !detail && <span>{c.subject}</span>}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={refresh}
                disabled={applying}
                className="text-sm bg-warmth-dark text-white px-3 py-1.5 rounded-full hover:bg-warmth transition font-medium disabled:opacity-60"
              >
                {applying ? '更新中…' : '更新'}
              </button>
              <button
                type="button"
                onClick={dismiss}
                disabled={applying}
                className="text-sm text-dusk hover:text-ink px-3 py-1.5 transition disabled:opacity-60"
              >
                稍后
              </button>
            </div>
          </div>
        </div>
      </div>
    </output>
  );
}
